import React, { useCallback } from 'react';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import { useApi } from '../../api/api';
import {
  aggregateSamsByYear,
  mapCasaToFisheryData,
  mapSamsToFisheryData,
  buildEconomicsByYear,
} from '../../utils/scallopFisheryDataMapper';

interface SamsParams {
  casaRunId?: number | null;
  inputDAS?: number;
  fleetSize?: number;
  nYears?: number;
  accessConfig?: Record<string, { mode: string; trips_per_vessel?: number; possession_limit_lbs?: number }>;
  lpueMethod?: string;
  mGeorgesBank?: number;
  mMidAtlantic?: number;
}

interface SamsProjectionCompleteData {
  scenarioId: number;
  samsData: unknown[];
  rawSamsData: unknown[];
  baselineSamsData?: unknown[];
  fisheryData: unknown[];
  scallopMetricSummary: unknown[];
  baselineScallopMetricSummary?: unknown[];
  dbParams: SamsParams;
}

interface SubmitSamsProjectionData {
  /** Override: called when user clicks Run SAMS Projection. If provided, replaces built-in logic. */
  onSubmitSams?: () => void | Promise<void>;
  /** Disabled when loading or updating. */
  submitSamsDisabled?: boolean;
}

function extractArray<T>(response: { data?: T | { success?: boolean; results?: T[] } }): T[] {
  const d = response?.data;
  if (Array.isArray(d)) return d;
  if (d && typeof d === 'object' && 'results' in d && Array.isArray((d as { results?: T[] }).results)) {
    return (d as { results: T[] }).results;
  }
  return [];
}

/**
 * Submit button for SAMS (scallop) projection.
 * Owns the run logic: reads params from PageDataContext, calls API, transforms results,
 * then invokes page callbacks to update state.
 */
export default function SubmitSamsProjection(props: RegistryComponentProps): React.ReactElement {
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<SubmitSamsProjectionData>(props);
  const {
    runSAMSProjection,
    getSamsAnnualSummary,
    getSamsExVesselValue,
    getSamsJobsImpact,
    getSamsMealsImpact,
    getSamsAreaSummaryTable,
  } = useApi();

  const overrideSubmit = data.onSubmitSams;
  const pageRunSams = pageData?.actions?.runSamsProjection as (() => void | Promise<void>) | undefined;
  const scallopLoading = (pageData?.data?.scallopLoading as boolean | undefined) ?? false;
  const updatingPStar = (pageData?.data?.updatingPStar as boolean | undefined) ?? false;
  const disabled = (data.submitSamsDisabled ?? updatingPStar ?? scallopLoading) ?? false;

  const runProjection = useCallback(async () => {
    const params = pageData?.data as Record<string, unknown> | undefined;
    const actions = pageData?.actions as Record<string, (...args: unknown[]) => void | Promise<void>> | undefined;

    const casaRunId = (params?.casaRunId as number | null) ?? null;
    const casaData = (params?.casaData as Array<{ year?: number }>) ?? [];
    const samsParams: SamsParams = {
      casaRunId,
      inputDAS: (params?.samsInputDAS as number) ?? 24,
      fleetSize: (params?.samsFleetSize as number) ?? 300,
      nYears: (params?.samsNYears as number) ?? 3,
      accessConfig: (params?.samsAccessConfig as SamsParams['accessConfig']) ?? {},
      lpueMethod: (params?.samsLpueMethod as string) ?? 'dynamic',
      mGeorgesBank: (params?.samsNaturalMortalityGB as number) ?? 0.2,
      mMidAtlantic: (params?.samsNaturalMortalityMA as number) ?? 0.25,
    };

    const setUpdatingPStar = actions?.setUpdatingPStar as ((v: boolean) => void) | undefined;
    const setRecalculationMessage = actions?.setRecalculationMessage as ((msg: string) => void) | undefined;
    const captureBaseline = actions?.captureBaseline as (() => void) | undefined;
    const setProviderData = actions?.setProviderData as ((fn: (prev: unknown) => unknown) => void) | undefined;
    const onSamsProjectionComplete = actions?.onSamsProjectionComplete as ((data: SamsProjectionCompleteData) => void) | undefined;
    const baselineSamsData = (params?.baselineSamsData as unknown[]) ?? [];
    const baselineScallopMetricSummary = (params?.baselineScallopMetricSummary as unknown[]) ?? [];

    try {
      setUpdatingPStar?.(true);
      setRecalculationMessage?.('Running SAMS projection...');
      captureBaseline?.();

      const response = await runSAMSProjection({
        scenario_name: `User_Projection_${Date.now()}`,
        casa_run_id: samsParams.casaRunId,
        n_years: samsParams.nYears,
        input_DAS: samsParams.inputDAS,
        fleet_size: samsParams.fleetSize,
        access_area_config: samsParams.accessConfig,
        lpue_method: samsParams.lpueMethod,
        m_georges_bank: samsParams.mGeorgesBank,
        m_mid_atlantic: samsParams.mMidAtlantic,
      });

      if (!response?.data?.success) {
        setProviderData?.((prev) => ({ ...(prev as Record<string, unknown>), error: 'Failed to run SAMS projection. Please try again.' }));
        return;
      }

      const newScenarioId = response.data.scenario_id;

      const samsSummaryResponse = await getSamsAnnualSummary(newScenarioId);
      let samsSummary: Array<Record<string, unknown>> = [];
      const sumData = samsSummaryResponse?.data;
      if (sumData && typeof sumData === 'object' && 'results' in sumData && Array.isArray((sumData as { results?: unknown[] }).results)) {
        samsSummary = (sumData as { results: Array<Record<string, unknown>> }).results;
      } else if (Array.isArray(sumData)) {
        samsSummary = sumData;
      }

      const aggregatedSams = aggregateSamsByYear(samsSummary);

      const exVesselData = extractArray(await getSamsExVesselValue(newScenarioId).catch(() => ({ data: [] })));
      const jobsData = extractArray(await getSamsJobsImpact(newScenarioId).catch(() => ({ data: [] })));
      const mealsData = extractArray(await getSamsMealsImpact(newScenarioId).catch(() => ({ data: [] })));

      const economicsByYear = buildEconomicsByYear(
        exVesselData as Array<Record<string, unknown>>,
        jobsData as Array<Record<string, unknown>>,
        mealsData as Array<Record<string, unknown>>
      );

      const casaRowsWithYear = casaData.filter((r): r is { year: number } => typeof r.year === 'number');
      const samsFisheryRows = mapSamsToFisheryData(aggregatedSams, economicsByYear);
      const casaFisheryRows = mapCasaToFisheryData(casaRowsWithYear);
      const scallopFisheryData = [...casaFisheryRows, ...samsFisheryRows];

      let metricsData: unknown[] = [];
      try {
        const metricsResponse = await getSamsAreaSummaryTable(newScenarioId);
        const md = metricsResponse?.data;
        if (md && typeof md === 'object' && 'area_summary' in md) {
          metricsData = (md as { area_summary?: unknown[] }).area_summary ?? [];
        } else if (md && typeof md === 'object' && 'success' in md && 'area_summary' in md) {
          metricsData = (md as { area_summary?: unknown[] }).area_summary ?? [];
        }
      } catch {
        // ignore
      }

      const newBaselineSams = baselineSamsData.length === 0 ? samsSummary : undefined;
      const newBaselineMetrics = baselineScallopMetricSummary.length === 0 ? metricsData : undefined;

      onSamsProjectionComplete?.({
        scenarioId: newScenarioId,
        samsData: aggregatedSams,
        rawSamsData: samsSummary,
        baselineSamsData: newBaselineSams,
        fisheryData: scallopFisheryData,
        scallopMetricSummary: metricsData,
        baselineScallopMetricSummary: newBaselineMetrics,
        dbParams: {
          inputDAS: samsParams.inputDAS,
          fleetSize: samsParams.fleetSize,
          nYears: samsParams.nYears,
          accessConfig: samsParams.accessConfig,
          lpueMethod: samsParams.lpueMethod,
        },
      });

      setRecalculationMessage?.('SAMS projection completed!');
      setTimeout(() => setRecalculationMessage?.(''), 3000);
    } catch (err) {
      console.error('Error running SAMS projection:', err);
      setProviderData?.((prev) => ({ ...(prev as Record<string, unknown>), error: 'Failed to run SAMS projection. Please try again.' }));
    } finally {
      setUpdatingPStar?.(false);
    }
  }, [
    pageData,
    runSAMSProjection,
    getSamsAnnualSummary,
    getSamsExVesselValue,
    getSamsJobsImpact,
    getSamsMealsImpact,
    getSamsAreaSummaryTable,
  ]);

  const onSubmit = overrideSubmit ?? pageRunSams ?? runProjection;

  const handleClick = useCallback(() => {
    if (typeof onSubmit === 'function') {
      void Promise.resolve(onSubmit());
    }
  }, [onSubmit]);

  return (
    <div className="noscroll-button-container" style={{ marginTop: '30px' }}>
      <button
        className="btn-primary noscroll-recalculate-all-button"
        onClick={handleClick}
        disabled={disabled}
      >
        Run SAMS Projection
      </button>
    </div>
  );
}
